/**
 * DeveloperApiClient
 * 
 * Handles developer app management and API keys
 */

import { BaseApiClient } from './BaseApiClient';

export class DeveloperApiClient extends BaseApiClient {
  /**
   * Create a new developer app
   */
  async createDeveloperApp(appData: {
    name: string;
    description?: string;
    contactEmail?: string;
    contactPhone?: string;
    contactName?: string;
  }): Promise<{
    success: boolean;
    app: {
      id: string;
      name: string;
      appId: string;
      apiKey: string;  // This is the client-key
      clientKey?: string;  // Backend might also return it as clientKey
      invitationCode: string;
      owner: string;
      createdAt: string;
    };
  }> {
    return this.post('/developer/apps', appData);
  }

  /**
   * Join a developer app using invitation code
   */
  async joinDeveloperApp(invitationCode: string): Promise<{
    success: boolean;
    app: {
      id: string;
      name: string;
      appId: string;
      role: string;
    };
  }> {
    return this.post('/developer/apps/join', { invitationCode });
  }

  /**
   * Get user's developer apps
   */
  async getUserDeveloperApps(): Promise<{
    ownedApps: Array<{
      id: string;
      name: string;
      appId: string;
      apiKey: string;
      invitationCode: string;
      description?: string;
      contactInfo?: {
        email?: string;
        phone?: string;
        name?: string;
      };
      createdAt: string;
      developers: Array<{
        userId: string;
        username: string;
        email: string;
        joinedAt: string;
      }>;
    }>;
    memberApps: Array<{
      id: string;
      name: string;
      appId: string;
      role: string;
      joinedAt: string;
      owner: {
        userId: string;
        username: string;
        email: string;
      };
    }>;
  }> {
    return this.get('/developer/apps');
  }

  /**
   * Get specific developer app details
   */
  async getDeveloperApp(appId: string): Promise<{
    app: {
      id: string;
      name: string;
      appId: string;
      apiKey: string;
      invitationCode: string;
      description?: string;
      contactInfo?: {
        email?: string;
        phone?: string;
        name?: string;
      };
      createdAt: string;
      lastUsed?: string;
      isActive: boolean;
      usage: {
        totalRequests: number;
        dailyRequests: number;
        monthlyRequests: number;
        lastRequestAt?: string;
      };
      developers: Array<{
        userId: string;
        username: string;
        email: string;
        role: string;
        joinedAt: string;
      }>;
    };
  }> {
    return this.get(`/developer/apps/${appId}`);
  }

  /**
   * Update developer app
   */
  async updateDeveloperApp(appId: string, updates: {
    name?: string;
    description?: string;
    contactEmail?: string;
    contactPhone?: string;
    contactName?: string;
  }): Promise<{
    success: boolean;
    message: string;
    app: any;
  }> {
    return this.put(`/developer/apps/${appId}`, updates);
  }

  /**
   * Delete developer app
   */
  async deleteDeveloperApp(appId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.delete(`/developer/apps/${appId}`);
  }

  /**
   * Regenerate API key
   */
  async regenerateApiKey(appId: string): Promise<{
    success: boolean;
    apiKey: string;  // This is the new client-key
    clientKey?: string;  // Backend might also return it as clientKey
  }> {
    return this.post(`/developer/apps/${appId}/api-key`);
  }

  /**
   * Regenerate invitation code
   */
  async regenerateInvitationCode(appId: string): Promise<{
    success: boolean;
    invitationCode: string;
  }> {
    return this.post(`/developer/apps/${appId}/invitation-code`);
  }

  /**
   * Update app configuration
   */
  async updateAppConfiguration(appId: string, config: any): Promise<any> {
    return this.put(`/developer/apps/${appId}/config`, config);
  }

  /**
   * Get app configuration
   */
  async getAppConfiguration(appId: string): Promise<any> {
    return this.get(`/developer/apps/${appId}/config`);
  }

  // ============================================
  // CLI and Script Management
  // ============================================

  /**
   * Create CLI session (WebSocket connection info)
   */
  async createCLISession(): Promise<{ sessionId: string; wsUrl: string }> {
    return this.post('/cli/session');
  }

  /**
   * Upload and execute script
   */
  async uploadScript(scriptData: {
    scriptContent: string;
    fileName?: string;
  }): Promise<{
    success: boolean;
    scriptId: string;
    message: string;
  }> {
    return this.post('/script/upload', scriptData);
  }

  /**
   * Get CLI command history
   */
  async getCLIHistory(limit: number = 20): Promise<{
    history: Array<{
      command: string;
      timestamp: string;
      executionTime: number;
      status: 'success' | 'error';
    }>;
  }> {
    return this.get(`/cli/history?limit=${limit}`);
  }

  /**
   * Execute CLI command
   */
  async executeCLICommand(command: string): Promise<{
    result: any;
    executionTime: number;
    status: 'success' | 'error';
    message: string;
  }> {
    return this.post('/cli/execute', { command });
  }

  /**
   * Remove developer from app
   */
  async removeDeveloperFromApp(appId: string, userId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.delete(`/developer/apps/${appId}/developers/${userId}`);
  }

  /**
   * Get app usage statistics
   */
  async getAppUsageStats(appId: string, period: '24h' | '7d' | '30d' | '90d' = '30d'): Promise<{
    period: string;
    totalRequests: number;
    successfulRequests: number;
    failedRequests: number;
    errorRate: number;
    averageResponseTime: number;
    requestsByEndpoint: Array<{
      endpoint: string;
      count: number;
      percentage: number;
    }>;
    requestsByDay: Array<{
      date: string;
      requests: number;
      errors: number;
    }>;
    responseTimesByEndpoint: Array<{
      endpoint: string;
      averageTime: number;
      maxTime: number;
      minTime: number;
    }>;
  }> {
    return this.get(`/developer/apps/${appId}/usage?period=${period}`);
  }

  /**
   * Get API rate limits
   */
  async getApiRateLimits(appId: string): Promise<{
    limits: {
      requestsPerMinute: number;
      requestsPerHour: number;
      requestsPerDay: number;
      requestsPerMonth: number;
    };
    current: {
      requestsThisMinute: number;
      requestsThisHour: number;
      requestsThisDay: number;
      requestsThisMonth: number;
    };
    resetTimes: {
      minuteReset: string;
      hourReset: string;
      dayReset: string;
      monthReset: string;
    };
  }> {
    return this.get(`/developer/apps/${appId}/rate-limits`);
  }

  /**
   * Get app API logs
   */
  async getAppApiLogs(appId: string, filters?: {
    startDate?: string;
    endDate?: string;
    endpoint?: string;
    method?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    logs: Array<{
      id: string;
      timestamp: string;
      method: string;
      endpoint: string;
      statusCode: number;
      responseTime: number;
      userAgent?: string;
      ipAddress?: string;
      requestSize?: number;
      responseSize?: number;
      error?: string;
    }>;
    total: number;
    hasMore: boolean;
  }> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.endpoint) params.append('endpoint', filters.endpoint);
    if (filters?.method) params.append('method', filters.method);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());
    
    return this.get(`/developer/apps/${appId}/logs?${params}`);
  }

  /**
   * Test API connection
   */
  async testApiConnection(appId: string): Promise<{
    success: boolean;
    responseTime: number;
    statusCode: number;
    message: string;
    timestamp: string;
  }> {
    return this.post(`/developer/apps/${appId}/test-connection`);
  }

  /**
   * Get app webhooks
   */
  async getAppWebhooks(appId: string): Promise<{
    webhooks: Array<{
      id: string;
      url: string;
      events: string[];
      active: boolean;
      secret: string;
      createdAt: string;
      lastTriggered?: string;
      failureCount: number;
    }>;
  }> {
    return this.get(`/developer/apps/${appId}/webhooks`);
  }

  /**
   * Create app webhook
   */
  async createAppWebhook(appId: string, webhookData: {
    url: string;
    events: string[];
    secret?: string;
  }): Promise<{
    success: boolean;
    webhook: {
      id: string;
      url: string;
      events: string[];
      active: boolean;
      secret: string;
    };
  }> {
    return this.post(`/developer/apps/${appId}/webhooks`, webhookData);
  }

  /**
   * Update app webhook
   */
  async updateAppWebhook(appId: string, webhookId: string, updates: {
    url?: string;
    events?: string[];
    active?: boolean;
  }): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.put(`/developer/apps/${appId}/webhooks/${webhookId}`, updates);
  }

  /**
   * Delete app webhook
   */
  async deleteAppWebhook(appId: string, webhookId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.delete(`/developer/apps/${appId}/webhooks/${webhookId}`);
  }

  /**
   * Test webhook
   */
  async testWebhook(appId: string, webhookId: string): Promise<{
    success: boolean;
    responseTime: number;
    statusCode: number;
    response: string;
    timestamp: string;
  }> {
    return this.post(`/developer/apps/${appId}/webhooks/${webhookId}/test`);
  }

  /**
   * Get webhook delivery logs
   */
  async getWebhookDeliveryLogs(appId: string, webhookId: string, limit: number = 50): Promise<{
    deliveries: Array<{
      id: string;
      timestamp: string;
      event: string;
      statusCode: number;
      responseTime: number;
      success: boolean;
      attempt: number;
      nextRetryAt?: string;
      response?: string;
      error?: string;
    }>;
    total: number;
  }> {
    return this.get(`/developer/apps/${appId}/webhooks/${webhookId}/deliveries?limit=${limit}`);
  }

  // ============================================
  // Billing Management
  // ============================================

  /**
   * Get billing configuration for developer app
   */
  async getBillingConfig(appId: string): Promise<{
    success: boolean;
    config: {
      model: 'client' | 'user' | 'hybrid';
      tokenPricing: Record<string, any>;
      quotas: Record<string, any>;
      billing: Record<string, any>;
      userCharges: Record<string, any>;
    };
  }> {
    return this.get(`/developer/apps/${appId}/billing/config`);
  }

  /**
   * Update token pricing
   */
  async updateTokenPricing(appId: string, pricing: {
    tokenType: string;
    basePrice: number;
    currency: string;
    tiers?: Array<{
      minQuantity: number;
      maxQuantity?: number;
      pricePerToken: number;
    }>;
  }): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.put(`/developer/apps/${appId}/billing/pricing`, pricing);
  }

  /**
   * Update quota configuration
   */
  async updateQuotas(appId: string, quotas: {
    enforcement: 'hard' | 'soft' | 'configurable';
    perUser?: Record<string, any>;
    client?: Record<string, any>;
  }): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.put(`/developer/apps/${appId}/billing/quotas`, quotas);
  }

  /**
   * Get current period usage
   */
  async getCurrentUsage(appId: string): Promise<{
    period: string;
    usage: Record<string, {
      count: number;
      cost: number;
      quota: {
        limit: number;
        used: number;
        remaining: number;
      };
      tier?: number;
    }>;
    totalCost: number;
    currency: string;
  }> {
    return this.get(`/developer/apps/${appId}/billing/usage/current`);
  }

  /**
   * Get historical usage
   */
  async getHistoricalUsage(appId: string, months: number = 12): Promise<{
    trends: {
      totalCost: number[];
      usageByType: Record<string, number[]>;
    };
    periods: string[];
  }> {
    return this.get(`/developer/apps/${appId}/billing/usage/history?months=${months}`);
  }

  /**
   * Get invoices list
   */
  async getInvoices(appId: string): Promise<{
    invoices: Array<{
      _id: string;
      invoiceNumber: string;
      billingPeriod: string;
      status: 'pending' | 'paid' | 'overdue' | 'cancelled';
      total: number;
      currency: string;
      dueDate: string;
      createdAt: string;
      usage: Record<string, {
        count: number;
        cost: number;
      }>;
    }>;
  }> {
    return this.get(`/developer/apps/${appId}/billing/invoices`);
  }

  /**
   * Get invoice details
   */
  async getInvoiceDetails(appId: string, invoiceId: string): Promise<{
    invoice: {
      _id: string;
      invoiceNumber: string;
      billingPeriod: string;
      status: string;
      total: number;
      subtotal: number;
      taxes?: number;
      currency: string;
      dueDate: string;
      createdAt: string;
      paidDate?: string;
      paymentMethod?: string;
      usage: Record<string, {
        count: number;
        cost: number;
      }>;
      pointsPayment?: {
        pointsUsed: number;
        pointsValue: number;
        fiatRemaining: number;
      };
    };
  }> {
    return this.get(`/developer/apps/${appId}/billing/invoices/${invoiceId}`);
  }

  /**
   * Pay invoice with Points
   */
  async payInvoiceWithPoints(appId: string, invoiceId: string, options?: {
    pointsAmount?: number;
    useAllAvailable?: boolean;
  }): Promise<{
    success: boolean;
    invoiceId: string;
    pointsUsed: number;
    pointsValue: number;
    remainingAmount: number;
    status: 'paid' | 'partially_paid';
    message: string;
  }> {
    return this.post(`/developer/apps/${appId}/billing/invoices/${invoiceId}/pay-with-points`, options || {});
  }

  /**
   * Configure user charges
   */
  async updateUserCharges(appId: string, userCharges: {
    cashTokenMinting?: {
      enabled: boolean;
      type: 'percentage' | 'fixed' | 'tiered';
      rate?: number;
      fixedAmount?: number;
      minFee?: number;
      maxFee?: number;
      currency: string;
    };
    cashTokenRedemption?: {
      enabled: boolean;
      type: 'percentage' | 'fixed' | 'tiered';
      rate?: number;
      fixedAmount?: number;
      minFee?: number;
      maxFee?: number;
      currency: string;
    };
    transferOut?: {
      enabled: boolean;
      paypal?: Record<string, any>;
      bankAccount?: Record<string, any>;
    };
  }): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.put(`/developer/apps/${appId}/billing/user-charges`, userCharges);
  }

  /**
   * Get user charges configuration
   */
  async getUserCharges(appId: string): Promise<{
    userCharges: Record<string, any>;
  }> {
    return this.get(`/developer/apps/${appId}/billing/user-charges`);
  }

  /**
   * Calculate user charge for an operation
   */
  async calculateUserCharge(appId: string, operation: {
    operation: string;
    amount: number;
    currency: string;
  }): Promise<{
    enabled: boolean;
    fee: number;
    currency: string;
    breakdown: {
      percentageFee?: number;
      fixedFee?: number;
      total: number;
    };
  }> {
    return this.post(`/developer/apps/${appId}/billing/user-charges/calculate`, operation);
  }

  /**
   * Export usage data
   */
  async exportUsage(appId: string, format: 'csv' | 'pdf' = 'csv'): Promise<Blob> {
    const response = await this.fetch(`/developer/apps/${appId}/billing/usage/export?format=${format}`, {
      method: 'GET',
    });
    return response.blob();
  }
} 